import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type ToggleSettingCardSize = 'small' | 'medium' | 'large';

@Component({
    selector: 'app-toggle-setting-card',
    templateUrl: './toggle-setting-card.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ToggleSettingCardComponent),
            multi: true
        }
    ],
    standalone: false
})
export class ToggleSettingCardComponent implements ControlValueAccessor {
    @Input() title = '';
    @Input() description = '';
    @Input() checked = false;
    @Input() disabled = false;
    @Input() size: ToggleSettingCardSize = 'medium';

    @Output() checkedChange = new EventEmitter<boolean>();
    @Output() toggled = new EventEmitter<boolean>();

    private onChange: (value: boolean) => void = () => undefined;
    private onTouched: () => void = () => undefined;

    get cardSizeClass(): string {
        switch (this.size) {
            case 'small':
                return 'min-h-10 gap-3 rounded-md px-3.5 py-2';
            case 'large':
                return 'min-h-12 gap-5 rounded-md p-4';
            default:
                return 'min-h-11 gap-4 rounded-md p-3';
        }
    }

    get titleClass(): string {
        switch (this.size) {
            case 'small':
                return 'text-xs';
            case 'large':
                return 'text-base';
            default:
                return 'text-sm';
        }
    }

    get descriptionClass(): string {
        switch (this.size) {
            case 'small':
                return 'text-[11px]';
            case 'large':
                return 'text-sm';
            default:
                return 'text-xs';
        }
    }

    get trackClass(): string {
        switch (this.size) {
            case 'small':
                return 'h-5 w-9';
            case 'large':
                return 'h-7 w-12';
            default:
                return 'h-6 w-11';
        }
    }

    get thumbClass(): string {
        switch (this.size) {
            case 'small':
                return 'h-4 w-4';
            case 'large':
                return 'h-6 w-6';
            default:
                return 'h-5 w-5';
        }
    }

    get thumbTranslateClass(): string {
        switch (this.size) {
            case 'small':
                return 'translate-x-4';
            case 'large':
                return 'translate-x-5';
            default:
                return 'translate-x-5';
        }
    }

    toggle(): void {
        if (this.disabled) {
            return;
        }

        const nextValue = !this.checked;
        this.checked = nextValue;
        this.onChange(nextValue);
        this.onTouched();
        this.checkedChange.emit(nextValue);
        this.toggled.emit(nextValue);
    }

    writeValue(value: boolean): void {
        this.checked = value === true;
    }

    registerOnChange(fn: (value: boolean) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
